import { HSLColor, RGBColor } from "react-color"
import tinycolor from "tinycolor2"

export const hexToRGBColor = (hex: string) => {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (result) {
        let RGBColor: RGBColor = {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
            a: 1,
        }
        return RGBColor
    } else {
        return {
            r: 0,
            g: 0,
            b: 0,
            a: 1,
        }
    }
}

export const hexToHSLColor = (hex: string) => {
    let RGBColor = hexToRGBColor(hex)

    let r = RGBColor.r
    let g = RGBColor.g
    let b = RGBColor.b
    r /= 255
    g /= 255
    b /= 255

    let max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h,
        s,
        l = (max + min) / 2
    if (max === min) {
        h = s = 0 // achromatic
    } else {
        let d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h ? (h /= 6) : (h = 0)
    }
    let HSLColor: HSLColor = {
        a: 1,
        h: h,
        l: l,
        s: s,
    }

    return HSLColor
}

/**
 * A color as stored in a style: `{ r, g, b, a }`, or (older graphics) any CSS color string —
 * `#fff`, `rgba(...)`, `transparent`, a named color. Parsed by tinycolor, like the
 * ChromePicker these fields replaced, so a graphic shows the same color it did there.
 */
export const toRGBColor = (value: unknown): RGBColor => {
    if (typeof value === "string") return tinycolor(value).toRgb()
    const color = (value ?? {}) as Partial<RGBColor>
    return {
        r: Number(color.r) || 0,
        g: Number(color.g) || 0,
        b: Number(color.b) || 0,
        a: color.a === undefined ? 1 : Number(color.a),
    }
}

/** `RRGGBB`, upper case and without the `#` — the way Figma shows a color. */
export const rgbToHex = ({ r, g, b }: RGBColor): string =>
    [r, g, b]
        .map((channel) =>
            Math.max(0, Math.min(255, Math.round(channel)))
                .toString(16)
                .padStart(2, "0")
        )
        .join("")
        .toUpperCase()

/**
 * What the user typed into a hex field, as a color with the given alpha — `#` optional, 3 or
 * 6 digits. Undefined when it is not a hex color, so the field can fall back.
 */
export const parseHexColor = (text: string, alpha: number): RGBColor | undefined => {
    const hex = text.trim().replace(/^#/, "")
    const full = /^[a-f\d]{3}$/i.test(hex) ? hex.replace(/./g, (digit) => digit + digit) : hex
    if (!/^[a-f\d]{6}$/i.test(full)) return undefined
    return { ...hexToRGBColor(full), a: alpha }
}
